// Item level color buckets — mirrors WoW item quality:
//   gray  : <70  (pre-TBC / heirloom-like)
//   green : 70-99 (quest greens / Karazhan pre-raid)
//   blue  : 100-114 (Kara/SSC/TK)
//   purple: 115-128 (Hyjal/BT)
//   orange: 129+ (Sunwell)
export function ilvlColor(ilvl) {
  if (!ilvl || ilvl < 70) return { hex: "#94a3b8", label: "Pre-raid", tier: "gray" };
  if (ilvl < 100) return { hex: "#1eff00", label: "Quest", tier: "green" };
  if (ilvl < 115) return { hex: "#0070dd", label: "T4 / Kara", tier: "blue" };
  if (ilvl < 129) return { hex: "#a335ee", label: "T5 / T6", tier: "purple" };
  return { hex: "#ff8000", label: "Sunwell", tier: "orange" };
}

// Per-slot weights for the Gear Score average — meta items count for less
// than chest/legs/weapon
const SLOT_WEIGHTS = {
  "Head": 1.0, "Neck": 0.6, "Shoulders": 1.0, "Back": 0.6,
  "Chest": 1.0, "Wrists": 0.6, "Hands": 1.0, "Waist": 0.6,
  "Legs": 1.0, "Feet": 0.8,
  "Finger 1": 0.5, "Finger 2": 0.5,
  "Trinket 1": 0.7, "Trinket 2": 0.7,
  "Main Hand": 1.2, "Off Hand": 0.8, "Ranged": 0.5,
};

// Weighted average ilvl across equipped slots
export function gearScore(gear) {
  if (!Array.isArray(gear)) return 0;
  let totalWeight = 0;
  let weightedSum = 0;
  for (const g of gear) {
    if (!g || !g.item_id || g.item_id <= 0) continue;
    const w = SLOT_WEIGHTS[g.slot] ?? 0.7;
    totalWeight += w;
    weightedSum += (g.item_level || 0) * w;
  }
  return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;
}

// Convert raw gear score into an A-F grade tied to TBC content tiers
export function gearGrade(score) {
  if (!score) return { letter: "—", color: "#64748b", label: "No data" };
  if (score >= 132) return { letter: "S",  color: "#ff8000", label: "Sunwell ready" };
  if (score >= 125) return { letter: "A+", color: "#a335ee", label: "BT geared" };
  if (score >= 120) return { letter: "A",  color: "#a335ee", label: "Hyjal/BT mix" };
  if (score >= 115) return { letter: "B+", color: "#a335ee", label: "T6 entering" };
  if (score >= 110) return { letter: "B",  color: "#0070dd", label: "T5 raider" };
  if (score >= 105) return { letter: "C+", color: "#0070dd", label: "SSC/TK ready" };
  if (score >= 100) return { letter: "C",  color: "#0070dd", label: "Kara cleared" };
  if (score >= 92)  return { letter: "D",  color: "#1eff00", label: "Pre-raid BiS" };
  return                   { letter: "F",  color: "#94a3b8", label: "Under-geared" };
}

// Identify the 3 weakest slots by ilvl gap from the player's mean
// (excluding obvious non-raid items like shirts — anything under ilvl 70 is gray
// "questing junk" per ilvlColor and can't realistically be a raid-gear slot)
export function weakestSlots(gear, count = 3) {
  if (!Array.isArray(gear) || gear.length === 0) return [];
  const equipped = gear.filter((g) => g.item_id > 0 && g.item_level >= 70);
  if (equipped.length < 3) return [];
  const mean = equipped.reduce((s, g) => s + g.item_level, 0) / equipped.length;
  return equipped
    .map((g) => ({ ...g, gap: Math.round(mean - g.item_level) }))
    .filter((g) => g.gap > 5)
    .sort((a, b) => b.gap - a.gap)
    .slice(0, count);
}
